// 🔧 resend_verification.js
// ✅ Purpose: Allow logged-in users to resend their email verification
// ✅ Features:
// - Uses the currently logged-in Firebase Auth user
// - No email/password form required
// - Does NOT sign the user out after resending
// - Supports the new workflow: Register → Dashboard → Verify Email → Continue Voting
// - Shows success and error notifications using the standard status helper
// - Provides clear elder-friendly copy
// - Sends users back to Dashboard after resend

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../../firebase";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import SeoHead from "../../components/seo_head";

const ResendVerification = () => {

  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [currentUser, setCurrentUser] = useState(auth.currentUser);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  const handleResendVerification = async () => {
    setStatus(null);

    if (!currentUser) {
      showStatus(setStatus, {
        type: "error",
        message: "Please log in first so we know where to send your verification email.",
      });

      return;
    }

    if (currentUser.emailVerified) {
      showStatus(setStatus, {
        type: "info",
        message: "Your email is already verified. You can continue voting.",
      });

      return;
    }

    try {
      setSending(true);

      await sendEmailVerification(currentUser, {
        url: process.env.REACT_APP_VERIFY_EMAIL_REDIRECT,
        handleCodeInApp: true,
      });

      showStatus(setStatus, {
        type: "success",
        message: "Verification email sent. Please check your email and click the link from Let's Go Vote.",
      });

      logger.log("📬 Verification email resent to current logged-in user.");
    } catch (err) {
      logger.error("❌ Verification - Error resending email:", { value: err });

      if (err.code === "auth/too-many-requests") {
        showStatus(setStatus, {
          type: "warning",
          message: "⚠️ Too many attempts. Please wait a few minutes, then try again.",
        });
      } else {
        showStatus(setStatus, {
          type: "error",
          message: "❌ Something went wrong sending the verification email. Please try again.",
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
  <>
    <SeoHead
      title="Resend Verification Email | Let's Go Vote"
      description="Resend your email verification link."
      path="/resend_verification"
      noindex={true}
    />
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">📩 Resend Verification Email</h1>

          <p className="lead text-muted">
            We can send a new verification link to the email address on your account.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {status?.message && (
          <div
            role="alert"
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-xs-12 col-md-7">
              <div className="bg-white p-4 rounded shadow text-start">
                {currentUser ? (
                  <>
                    <div className="alert alert-info">
                      <strong>Almost done!</strong>
                      <br />
                      We will send your verification email to:
                      <br />
                      <strong>{currentUser.email}</strong>
                    </div>

                    <p className="text-muted">
                      After you click the verification link in your email, return here to continue.
                    </p>

                    <div className="d-grid gap-2 mb-3">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleResendVerification}
                        disabled={sending || currentUser.emailVerified}
                      >
                        {sending ? "Sending..." : "Send Verification Email"}
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate("/dashboard?verifyRequired=1")}
                      >
                        Back to Dashboard
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="alert alert-warning">
                      Please log in first so we know which account needs a verification email.
                    </div>

                    <div className="d-grid gap-2">
                      <Link to="/login" className="btn btn-primary">
                        Go to Login
                      </Link>

                      <Link to="/register" className="btn btn-outline-secondary">
                        Create Account
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
  );
};

export default ResendVerification;